/**
 * HumanInputMessage Component
 *
 * Renders llm-runtime ask_user_input prompts as UI-only chat controls.
 * Supports single-select, multiple-select, question-scoped Other input, and
 * explicit cancellation under the llm-runtime 0.7 contract.
 *
 * Recent changes:
 * - Uses a bounded multiline Other control and preserves normalized internal whitespace.
 * - Fails closed for malformed allowOther values instead of rendering free-form controls.
 * - Emits canonical answered/cancelled outcomes and gates Other by allowOther.
 * - Renders client-owned ask_user_input assistant tool calls.
 */

import { useMemo, useState } from 'react';
import {
  ChatMessage,
  HUMAN_INPUT_FREEFORM_MAX_LENGTH,
  HumanInputAnswer,
  HumanInputQuestion,
  HumanInputRequest
} from '../../models/ChatMessage';
import Button from '../primitives/Button';
import { normalizeHumanInputFreeform } from '../../utils/humanInputContract';

interface HumanInputMessageProps {
  message: ChatMessage;
  disabled?: boolean;
  onSubmit: (messageId: number, answer: HumanInputAnswer) => void;
}

function getRequest(message: ChatMessage): HumanInputRequest | null {
  const request = message.eventData?.details?.request;
  if (!request || !Array.isArray(request.questions)) {
    return null;
  }

  return request as HumanInputRequest;
}

function hasAnswered(message: ChatMessage): boolean {
  const status = message.eventData?.status;
  return status === 'success' || status === 'cancelled';
}

function createEmptySelections(questions: HumanInputQuestion[], multiple: boolean): Record<string, string | string[]> {
  return Object.fromEntries(questions.map((question) => [question.id, multiple ? [] : '']));
}

export default function HumanInputMessage({ message, disabled = false, onSubmit }: HumanInputMessageProps) {
  const request = getRequest(message);
  const selectionType = request?.type ?? 'single-select';
  const isMultiple = selectionType === 'multiple-select';
  const answered = hasAnswered(message);
  const cancelled = message.eventData?.status === 'cancelled';
  const questions = useMemo(() => request?.questions ?? [], [request]);
  const [selections, setSelections] = useState<Record<string, string | string[]>>(() =>
    createEmptySelections(questions, isMultiple)
  );
  const [otherAnswers, setOtherAnswers] = useState<Record<string, string>>({});

  const normalizedSelections = useMemo(() => {
    const normalized = Object.create(null) as Record<string, string | string[]>;

    for (const question of questions) {
      const value = selections[question.id];
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return null;
        }
        normalized[question.id] = value;
        continue;
      }

      if (question.options.some((option) => option.id === value)) {
        normalized[question.id] = value;
        continue;
      }

      if (question.allowOther !== true) {
        return null;
      }

      const freeform = normalizeHumanInputFreeform(value ?? '');
      if (!freeform) {
        return null;
      }
      normalized[question.id] = freeform;
    }

    return normalized;
  }, [questions, selections]);
  const canSubmit = normalizedSelections !== null;

  if (!request || questions.length === 0) {
    return null;
  }

  const submit = (answer: HumanInputAnswer) => {
    if (!answered && !disabled) {
      onSubmit(message.id, answer);
    }
  };

  const submitCurrentSelections = () => {
    if (!normalizedSelections) {
      return;
    }

    submit({ status: 'answered', answers: normalizedSelections });
  };

  const selectSingle = (questionId: string, optionId: string) => {
    const nextSelections = {
      ...selections,
      [questionId]: optionId
    };
    setSelections(nextSelections);
    setOtherAnswers((current) => ({ ...current, [questionId]: '' }));

    if (questions.length === 1) {
      submit({ status: 'answered', answers: nextSelections });
    }
  };

  const toggleMultiple = (questionId: string, optionId: string) => {
    const current = selections[questionId];
    const currentValues = Array.isArray(current) ? current : [];
    const nextValues = currentValues.includes(optionId)
      ? currentValues.filter((value) => value !== optionId)
      : [...currentValues, optionId];

    setSelections({
      ...selections,
      [questionId]: nextValues
    });
  };

  const setOtherAnswer = (questionId: string, value: string) => {
    setOtherAnswers((current) => ({ ...current, [questionId]: value }));
    setSelections({
      ...selections,
      [questionId]: value
    });
  };

  return (
    <div className="flex justify-start">
      <div className="max-w-3xl rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm dark:border-blue-800 dark:bg-blue-950/30">
        <div className="mb-3 flex items-center gap-2 text-blue-900 dark:text-blue-100">
          <span className="h-2 w-2 rounded-full bg-blue-500" aria-hidden="true" />
          <span className="font-medium">
            {cancelled ? 'Input skipped' : answered ? 'Input recorded' : 'Input needed'}
          </span>
        </div>

        <div className="space-y-4">
          {questions.map((question) => {
            const selectedValue = selections[question.id];
            const selectedValues = Array.isArray(selectedValue) ? selectedValue : selectedValue ? [selectedValue] : [];

            return (
              <fieldset key={question.id} disabled={answered || disabled} className="space-y-2">
                <legend className="text-xs font-semibold uppercase text-blue-700 dark:text-blue-300">
                  {question.header}
                </legend>
                <p className="text-sm text-gray-900 dark:text-gray-100">{question.question}</p>
                <div className="grid grid-cols-1 gap-2">
                  {question.options.map((option) => {
                    const selected = selectedValues.includes(option.id);
                    const optionClasses = selected
                      ? 'border-blue-500 bg-white text-blue-950 dark:border-blue-400 dark:bg-blue-900/40 dark:text-blue-50'
                      : 'border-gray-200 bg-white text-gray-800 hover:border-blue-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:border-blue-600';

                    return (
                      <button
                        key={option.id}
                        type="button"
                        disabled={answered || disabled}
                        onClick={() => {
                          if (isMultiple) {
                            toggleMultiple(question.id, option.id);
                          } else {
                            selectSingle(question.id, option.id);
                          }
                        }}
                        className={`rounded-md border px-3 py-2 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-70 ${optionClasses}`}
                      >
                        <span className="block text-sm font-medium">{option.label}</span>
                        {option.description && (
                          <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">{option.description}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {!isMultiple && question.allowOther === true && (
                  <textarea
                    value={otherAnswers[question.id] ?? ''}
                    maxLength={HUMAN_INPUT_FREEFORM_MAX_LENGTH}
                    rows={2}
                    onChange={(event) => setOtherAnswer(question.id, event.target.value)}
                    placeholder="Other answer"
                    aria-label={`${question.header} other answer`}
                    className="block min-w-0 max-w-full w-full resize-y rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-70 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                  />
                )}
              </fieldset>
            );
          })}
        </div>

        {!answered && (
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            {request.allowSkip && (
              <Button
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => submit({ status: 'cancelled', reason: 'skipped' })}
              >
                Skip
              </Button>
            )}
            {(isMultiple || questions.length > 1 || questions.some((question) => question.allowOther === true)) && (
              <Button
                variant="primary"
                size="sm"
                disabled={disabled || !canSubmit}
                onClick={submitCurrentSelections}
              >
                Submit
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
